import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { hasAnyRole } from "../auth/authUi";
import { CMMS_FIELD_ROLES } from "../features/cmms/cmmsRoles";
import { useIsCmmsEnabled } from "../features/cmms/cmmsFeature";
import { foldWorstByToolId } from "../features/cmms/toolServiceStatus";
import { fetchToolServiceStatuses } from "../fetchers/cmmsToolMonitoring";
import { useLocalStorage } from "./useLocalStorage";

/*
 * How far each watched tool has run against its service schedule, keyed by tool id.
 *
 * Shared with the service history panel and the machine page badge rather than inlined into the
 * tools grid: all three ask the same question of the same endpoint, and two copies of a read is
 * how the answers drift.
 *
 * The fold to one row per tool lives in toolServiceStatus.js and mirrors the server's own
 * "worst wins" rule — see foldWorstByToolId.
 */
export function useToolServiceStatuses(toolId, { enabled = true } = {}) {
  const { roleIds } = useAuth();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const cmmsEnabled = useIsCmmsEnabled();

  const isCmmsUser = hasAnyRole(roleIds, CMMS_FIELD_ROLES);

  const query = useQuery(
    ["cmmsToolServiceStatuses", companyKey, toolId ?? null],
    () => fetchToolServiceStatuses(companyKey, toolId),
    {
      // Both gates earn their place. Without the module switch every OEE customer that has
      // never opted into CMMS would fire this from the tool register, which they use daily,
      // for a module they cannot see. Without the role check the request 403s for anyone
      // outside Cmms_Field — which cannot happen on /tools since that route is [1,2] and the
      // policy now includes 2, but does happen on the wider pages the machine badge mounts on.
      // `enabled` is the caller's own gate, on top of the two above. That badge needs it because
      // omitting toolId does not mean "no tool" — it means "every tool in the company", which
      // is exactly what the tools grid wants and exactly what a machine page must not fire
      // while it is still resolving which single tool it means.
      enabled: enabled && cmmsEnabled && isCmmsUser && Boolean(companyKey),
      staleTime: 60000,
      keepPreviousData: true,
    }
  );

  const statusByToolId = useMemo(() => foldWorstByToolId(query.data), [query.data]);

  return {
    /*
     * The readings as they arrived, unfolded.
     *
     * The fold above answers "how is this tool doing", which is all a badge or a grid cell has
     * room for. The CMMS tools page needs the other question too — *which* schedules watch this
     * tool — and a tool can be watched by two, one in hours and one in cycles. Handing back the
     * list it already holds is what keeps that caller from firing a second read, or from
     * unpicking the Map to guess at what the fold dropped.
     *
     * Still in the server's order: worst first across the whole company, so worst first within
     * each tool as well.
     */
    statuses: query.data ?? [],
    statusByToolId,
    // isInitialLoading, not isLoading. A disabled query stays status:"loading" forever in
    // react-query v4, so isLoading would leave every cell showing a skeleton for a customer
    // who simply has CMMS switched off.
    isLoading: query.isInitialLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useToolServiceStatuses;
